import api from '@/utils/axios';
import { unwrapItem, unwrapList } from '@/utils/apiHelpers';

export interface AdminDashboardStats {
  total_users: number;
  active_users: number;
  pending_users: number;
  suspended_users: number;
  total_clients: number;
  active_drivers: number;
  community_drivers: number;
  professional_drivers: number;
  pending_documents: number;
  total_zones: number;
  total_orders: number;
  today_orders: number;
  month_orders: number;
  active_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  total_revenue: number;
  today_revenue: number;
  month_revenue: number;
  open_disputes: number;
  in_review_disputes: number;
  resolved_disputes: number;
  open_support_requests: number;
  late_orders: number;
  average_satisfaction: number;
  active_promotions: number;
  relay_points: number;
  active_admins?: number;
  cities_covered?: number;
}

export interface AdminNotification {
  id: string;
  type: string;
  titre: string;
  message: string;
  count: number;
}

export interface AdminDashboardData {
  stats: AdminDashboardStats;
  recent_users: Array<{
    id: number;
    name: string;
    email: string | null;
    phone: string;
    status: string;
    type?: string;
    zone?: string;
    created_at?: string;
  }>;
  pending_documents: Array<{
    id: number;
    category?: string;
    user_name?: string;
    user_id: number;
    status: string;
  }>;
  pending_orders: AdminOrderSummary[];
  late_orders_list: AdminOrderSummary[];
  open_disputes_list: AdminDisputeSummary[];
  users_by_zone: Array<{
    zone_id: number;
    zone_name: string;
    users_count: number;
    active_users: number;
  }>;
  notifications: AdminNotification[];
  staff_preview?: Array<{
    id: number;
    name: string;
    email: string | null;
    status: string;
    last_login?: string | null;
  }>;
  system_health?: Array<{ name: string; status: string; uptime: string }>;
}

export interface AdminOrderSummary {
  id: number;
  code: string;
  status: string;
  client_name?: string | null;
  client_phone?: string | null;
  driver_name?: string | null;
  delivery_type?: string | null;
  departure_zone?: string | null;
  arrival_zone?: string | null;
  departure_address?: string;
  arrival_address?: string;
  package_weight?: number;
  price: number;
  payment_status?: string | null;
  created_at?: string;
}

export interface AdminDisputeSummary {
  id: number;
  order_id: number;
  order_code: string;
  client_name?: string | null;
  driver_name?: string | null;
  reason: string;
  description?: string | null;
  status: string;
  resolution?: string | null;
  created_at?: string;
}

export interface AdminPromotion {
  id: number;
  code: string;
  description?: string | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_uses?: number | null;
  used_count: number;
  valid_from?: string | null;
  valid_until?: string | null;
  active: boolean;
}

export interface AdminPromotionStats {
  total: number;
  active: number;
  total_uses: number;
  estimated_savings: number;
}

export interface AdminRelayPoint {
  id: number;
  name: string;
  address: string;
  secteur_id?: number | null;
  secteur_name?: string | null;
  zone_name?: string | null;
  manager_name?: string | null;
  manager_phone?: string | null;
  opening_hours?: string | null;
  is_active: boolean;
}

export interface AdminSupportRequest {
  id: number;
  user_id: number;
  user_name?: string | null;
  user_phone?: string | null;
  subject: string;
  message: string;
  status: string;
  admin_notes?: string | null;
  assigned_to?: string | null;
  created_at?: string;
  resolved_at?: string | null;
}

export interface AdminReportsData {
  period: string;
  orders_count: number;
  completed_orders: number;
  cancelled_orders: number;
  revenue: number;
  average_delivery_hours: number;
  average_satisfaction: number;
  orders_by_status: Array<{ status: string; count: number }>;
  revenue_by_zone: Array<{ zone: string; revenue: number; orders: number }>;
  top_drivers: Array<{ driver_id?: number; driver_name?: string; deliveries: number; revenue: number }>;
}

export async function fetchAdminDashboard(extended = false): Promise<AdminDashboardData> {
  const res = await api.get('/admin/dashboard', { params: extended ? { extended: 1 } : undefined });
  return unwrapItem<AdminDashboardData>(res.data);
}

export async function fetchAdminReports(period: 'today' | 'week' | 'month' = 'month'): Promise<AdminReportsData> {
  const res = await api.get('/admin/reports', { params: { period } });
  return unwrapItem<AdminReportsData>(res.data);
}

export async function fetchAdminOrders(status?: string): Promise<AdminOrderSummary[]> {
  const res = await api.get('/admin/orders', { params: status ? { status } : undefined });
  return unwrapList<AdminOrderSummary>(res.data);
}

export async function fetchAdminDisputes(status?: string): Promise<AdminDisputeSummary[]> {
  const res = await api.get('/admin/disputes', { params: status ? { status } : undefined });
  return unwrapList<AdminDisputeSummary>(res.data);
}

export async function createAdminDispute(payload: {
  order_request_id: number;
  reason: string;
  description?: string;
}): Promise<AdminDisputeSummary> {
  const res = await api.post('/admin/disputes', payload);
  return unwrapItem<AdminDisputeSummary>(res.data);
}

export async function resolveAdminDispute(
  id: number,
  payload: { resolution: string; resolution_notes?: string; new_driver_id?: number },
): Promise<AdminDisputeSummary> {
  const res = await api.post(`/admin/disputes/${id}/resolve`, payload);
  return unwrapItem<AdminDisputeSummary>(res.data);
}

export async function fetchAdminPromotions(): Promise<{ data: AdminPromotion[]; stats: AdminPromotionStats }> {
  const res = await api.get('/admin/promotions');
  return {
    data: unwrapList<AdminPromotion>(res.data),
    stats: (res.data.stats ?? res.data.data?.stats) as AdminPromotionStats,
  };
}

export async function createAdminPromotion(payload: Partial<AdminPromotion>): Promise<AdminPromotion> {
  const res = await api.post('/admin/promotions', payload);
  return unwrapItem<AdminPromotion>(res.data);
}

export async function updateAdminPromotion(id: number, payload: Partial<AdminPromotion>): Promise<AdminPromotion> {
  const res = await api.patch(`/admin/promotions/${id}`, payload);
  return unwrapItem<AdminPromotion>(res.data);
}

export async function deleteAdminPromotion(id: number): Promise<void> {
  await api.delete(`/admin/promotions/${id}`);
}

export async function fetchAdminRelayPoints(): Promise<AdminRelayPoint[]> {
  const res = await api.get('/admin/relay-points');
  return unwrapList<AdminRelayPoint>(res.data);
}

export async function createAdminRelayPoint(payload: Partial<AdminRelayPoint>): Promise<AdminRelayPoint> {
  const res = await api.post('/admin/relay-points', payload);
  return unwrapItem<AdminRelayPoint>(res.data);
}

export async function deleteAdminRelayPoint(id: number): Promise<void> {
  await api.delete(`/admin/relay-points/${id}`);
}

export async function fetchAdminSupportRequests(status?: string): Promise<AdminSupportRequest[]> {
  const res = await api.get('/admin/support', { params: status ? { status } : undefined });
  return unwrapList<AdminSupportRequest>(res.data);
}

export async function updateAdminSupportRequest(
  id: number,
  payload: { status?: string; admin_notes?: string },
): Promise<AdminSupportRequest> {
  const res = await api.patch(`/admin/support/${id}`, payload);
  return unwrapItem<AdminSupportRequest>(res.data);
}

export async function assignAdminSupportRequest(id: number): Promise<AdminSupportRequest> {
  const res = await api.post(`/admin/support/${id}/assign`);
  return unwrapItem<AdminSupportRequest>(res.data);
}

export interface AdminStaffMessage {
  id: number;
  sender_id: number;
  sender_name?: string | null;
  recipient_id: number;
  recipient_name?: string | null;
  subject: string;
  body: string;
  is_mine: boolean;
  read_at?: string | null;
  created_at?: string;
}

export async function fetchAdminStaffMessages(withUserId?: number): Promise<{ messages: AdminStaffMessage[]; unread_count: number }> {
  const res = await api.get('/admin/messages', { params: withUserId ? { with: withUserId } : undefined });
  return {
    messages: unwrapList<AdminStaffMessage>(res.data),
    unread_count: (res.data.unread_count ?? 0) as number,
  };
}

export async function sendAdminStaffMessage(payload: {
  recipient_id: number;
  subject: string;
  body: string;
}): Promise<AdminStaffMessage> {
  const res = await api.post('/admin/messages', payload);
  return unwrapItem<AdminStaffMessage>(res.data);
}

export async function markAdminStaffMessageRead(id: number): Promise<AdminStaffMessage> {
  const res = await api.post(`/admin/messages/${id}/read`);
  return unwrapItem<AdminStaffMessage>(res.data);
}
